// Generated from Swagger on 2025-08-27T14:46:24.226Z
import {
  useSuspenseQuery,
  useQuery,
  useMutation,
  useQueryClient,
  UseSuspenseQueryOptions,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { useApiClient, ApiRequestOptions } from "../client";
import { creationEntitiesKeys } from "../creation-entities-keys";
import { formatSearchQuery } from "../../utils/format-search-query";
import {
  MetaLoginUrlResponseDto,
  PostCreationEntitiesResponseDto,
  UMetapostToAccountFormData,
} from "../interfaces/integrations.interface";

export interface UseMetahandleRedirectParams {
  code: string;
  state: string;
}

// Query keys for integrations-related queries
export const integrationsKeys = {
  all: ["integrations"] as const,
  meta_oauth_start: (params?: { rerequest?: boolean }) =>
    [...integrationsKeys.all, "meta_oauth_start", params] as const,
  meta_oauth_callback: (params?: any) =>
    [...integrationsKeys.all, "meta_oauth_callback", params] as const,
  creation_entities: () =>
    [...integrationsKeys.all, "creation_entities"] as const,
  meta_post_mutation: () =>
    [...integrationsKeys.all, "meta_post_mutation"] as const,
};

/**
 * Get the login URL for Meta (ONLY ADMIN)
 */
export function useMetastartOAuth(
  params?: { rerequest?: boolean },
  options?: Omit<
    UseSuspenseQueryOptions<MetaLoginUrlResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [
      ...integrationsKeys.meta_oauth_start(params),
      ...creationEntitiesKeys.list(),
    ],
    queryFn: () => {
      const query = params?.rerequest ? "?rerequest=true" : "";
      return apiRequest<MetaLoginUrlResponseDto>(
        `/integrations/meta/oauth/start${query}`,
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useMetastartOAuth
 */
export function useMetastartOAuthQuery(
  params?: { rerequest?: boolean },
  options?: Omit<
    UseQueryOptions<MetaLoginUrlResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [
      ...integrationsKeys.meta_oauth_start(params),
      ...creationEntitiesKeys.list(),
    ],
    queryFn: () => {
      const query = params?.rerequest ? "?rerequest=true" : "";
      return apiRequest<MetaLoginUrlResponseDto>(
        `/integrations/meta/oauth/start${query}`,
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * GET /integrations/meta/oauth/callback
 */
export function useMetahandleRedirect(
  params?: UseMetahandleRedirectParams,
  options?: Omit<UseSuspenseQueryOptions<any>, "queryKey" | "queryFn">,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [
      ...integrationsKeys.meta_oauth_callback({ ...params }),
      ...creationEntitiesKeys.list(),
    ],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<any>(`/integrations/meta/oauth/callback${queryString}`);
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useMetahandleRedirect
 */
export function useMetahandleRedirectQuery(
  params?: UseMetahandleRedirectParams,
  options?: Omit<UseQueryOptions<any>, "queryKey" | "queryFn">,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [
      ...integrationsKeys.meta_oauth_callback({ ...params }),
      ...creationEntitiesKeys.list(),
    ],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<any>(`/integrations/meta/oauth/callback${queryString}`);
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Get the post creation entities for the user
 */
export function useMetaGetPostCreationEntities(
  options?: Omit<
    UseSuspenseQueryOptions<PostCreationEntitiesResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: creationEntitiesKeys.list(),
    queryFn: () =>
      apiRequest<PostCreationEntitiesResponseDto>(
        `/integrations/meta/post/creation-entities`,
      ),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useMetaGetPostCreationEntities
 */
export function useMetaGetPostCreationEntitiesQuery(
  options?: Omit<
    UseQueryOptions<PostCreationEntitiesResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: creationEntitiesKeys.list(),
    queryFn: () =>
      apiRequest<PostCreationEntitiesResponseDto>(
        `/integrations/meta/post/creation-entities`,
      ),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Post to Facebook - max 50MB
 */
export function useMetapostToAccount(
  options?: Omit<
    UseMutationOptions<any, Error, UMetapostToAccountFormData>,
    "mutationFn"
  >,
  apiOptions?: ApiRequestOptions
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data: UMetapostToAccountFormData) => {
      const formData = new FormData();
      if (data.file) formData.append("file", data.file);
      if (data.pageId !== undefined)
        formData.append("pageId", String(data.pageId));
      if (data.message !== undefined)
        formData.append("message", String(data.message));
      if (data.instagramPost !== undefined)
        formData.append("instagramPost", String(data.instagramPost));
      if (data.facebookPost !== undefined)
        formData.append("facebookPost", String(data.facebookPost));
      if (data.instagramStory !== undefined)
        formData.append("instagramStory", String(data.instagramStory));
      if (data.facebookStory !== undefined)
        formData.append("facebookStory", String(data.facebookStory));
      return apiRequest<any>(
        `/integrations/meta/post`, 
        "POST", 
        formData, 
        undefined, 
        apiOptions
      );
    },
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: integrationsKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      // Call user-provided onSuccess if it exists
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Disconnect Meta integration (ONLY ADMIN)
 */
export function useMetaDisconnectMutation(
  options?: Omit<UseMutationOptions<any, Error, void>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: () => apiRequest<any>(`/integrations/meta/disconnect`, "POST"),
    onSuccess: (data, variables, context) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: integrationsKeys.all });
      queryClient.invalidateQueries({ queryKey: creationEntitiesKeys.list() });
      options?.onSuccess?.(data, variables, context);
    },
  });
}
